import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, canNamed } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import "./App.css";

// Phase 4N — Route-level code splitting. Heavy pages are dynamically imported
// so a fresh visitor only downloads what they actually need:
//   • Collection agent → Dashboard + Leads
//   • Performer agent  → Dashboard + Performer
//   • Admin            → Dashboard + Admin (+ Client 360 lazy-loaded inside it)
// Webpack/CRA emits a separate JS chunk per lazy() call.
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const AllLeadsPage = lazy(() => import("./pages/AllLeadsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const MastersPage = lazy(() => import("./pages/MastersPage"));
const ProgramHierarchyPage = lazy(() => import("./pages/ProgramHierarchyPage"));
const MergeReviewsPage = lazy(() => import("./pages/MergeReviewsPage"));
const PerformerPage = lazy(() => import("./pages/PerformerPage"));
const ForceChangePasswordPage = lazy(() => import("./pages/ForceChangePasswordPage"));
const ProgramDashboard = lazy(() => import("./pages/ProgramDashboard"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const MyPerformancePage = lazy(() => import("./pages/MyPerformance"));
const ITDashboard = lazy(() => import("./pages/ITDashboard"));
const FailedPaymentsPage = lazy(() => import("./pages/FailedPaymentsPage"));
const PendingAlignmentPage = lazy(() => import("./pages/PendingAlignmentPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const CallReportsPage = lazy(() => import("./pages/CallReportsPage"));
const MyBucketPage = lazy(() => import("./pages/MyBucketPage"));
const LeadTransfersPage = lazy(() => import("./pages/LeadTransfersPage"));
const ClaimAttendeePage = lazy(() => import("./pages/ClaimAttendeePage"));
const RoutingPreviewPage = lazy(() => import("./pages/RoutingPreviewPage"));
const StatusImportPage = lazy(() => import("./pages/StatusImportPage"));
const CurrentCustomerImportPage = lazy(() => import("./pages/CurrentCustomerImportPage"));
const ExtendedImportPage = lazy(() => import("./pages/ExtendedImportPage"));
const ParticipantImportPage = lazy(() => import("./pages/ParticipantImportPage"));
const MyDataPage = lazy(() => import("./pages/MyDataPage"));
const LeadAssignmentImportPage = lazy(() => import("./pages/LeadAssignmentImportPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ReportingPage = lazy(() => import("./pages/ReportingPage"));
const CoachOverlapPage = lazy(() => import("./pages/CoachOverlapPage"));
const UniquePeoplePage = lazy(() => import("./pages/UniquePeoplePage"));
const RefundRequestsPage = lazy(() => import("./pages/RefundRequestsPage"));
const LeadDetailPage = lazy(() => import("./pages/LeadDetailPage"));
const RolesPage = lazy(() => import("./pages/RolesPage"));
const TeamDashboardPage = lazy(() => import("./pages/TeamDashboardPage"));
const FinanceOperationsPage = lazy(() => import("./pages/FinanceOperationsPage"));
const FocusModePage = lazy(() => import("./pages/FocusModePage"));
const NewTokensPage = lazy(() => import("./pages/NewTokensPage"));
const DiscountApprovalsPage = lazy(() => import("./pages/DiscountApprovalsPage"));
const AssignLeadsPage = lazy(() => import("./pages/AssignLeadsPage"));

const PageFallback = () => (
  <div className="loading-screen" data-testid="route-suspense">Loading…</div>
);

function ProtectedRoute({ children, adminOnly = false, performerAllowed = false, financeAllowed = false, namedPerm = null }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return <div className="loading-screen">Loading...</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // Mandatory password change blocker — no other route is accessible until
  // the user has set a personal password.
  if (user.must_change_password) {
    return (
      <Suspense fallback={<PageFallback />}>
        <ForceChangePasswordPage />
      </Suspense>
    );
  }
  if (adminOnly && user.role !== "admin" && !(financeAllowed && user.role === "finance")) {
    return <Navigate to="/" replace />;
  }
  // If page is performer-allowed, allow admin, performer_agent role, or any user in the Performer team
  const canAccessPerformer =
    user.role === "admin" ||
    user.role === "performer_agent" ||
    (user.teams || []).includes("Performer") ||
    user.team_type === "Performer";
  if (performerAllowed && !canAccessPerformer) {
    return <Navigate to="/leads" replace />;
  }
  // Named-permission gate — blocks sub-admins/restricted roles from sensitive pages
  // namedPerm accepts a single perm string or an array (any-of passes).
  if (namedPerm) {
    const perms = Array.isArray(namedPerm) ? namedPerm : [namedPerm];
    if (!perms.some((p) => canNamed(user, p))) {
      return <Navigate to="/" replace />;
    }
  }
  return (
    <Layout>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </Layout>
  );
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="loading-screen">Loading...</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/leads/:leadId" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/all-leads" element={<ProtectedRoute><AllLeadsPage /></ProtectedRoute>} />
      <Route path="/leads-board" element={<ProtectedRoute><AllLeadsPage forcedCustomer={false} /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><AllLeadsPage forcedCustomer={true} /></ProtectedRoute>} />
      <Route path="/my-performance" element={<ProtectedRoute><MyPerformancePage /></ProtectedRoute>} />
      <Route path="/my-bucket" element={<ProtectedRoute><MyBucketPage /></ProtectedRoute>} />
      <Route path="/lead-transfers" element={<ProtectedRoute><LeadTransfersPage /></ProtectedRoute>} />
      <Route path="/claims" element={<ProtectedRoute><ClaimAttendeePage /></ProtectedRoute>} />
      <Route path="/operations/new-tokens" element={<ProtectedRoute adminOnly><NewTokensPage /></ProtectedRoute>} />
      <Route path="/operations/approvals" element={<ProtectedRoute namedPerm={["discounts.approve", "plans.approve"]}><DiscountApprovalsPage /></ProtectedRoute>} />
      <Route path="/operations/assign-leads" element={<ProtectedRoute adminOnly><AssignLeadsPage /></ProtectedRoute>} />
      <Route path="/routing-preview" element={<ProtectedRoute><RoutingPreviewPage /></ProtectedRoute>} />
      <Route path="/status-import" element={<ProtectedRoute><StatusImportPage /></ProtectedRoute>} />
      <Route path="/current-customer" element={<ProtectedRoute><CurrentCustomerImportPage /></ProtectedRoute>} />
      <Route path="/extended-import" element={<ProtectedRoute><ExtendedImportPage /></ProtectedRoute>} />
      <Route path="/participant-import" element={<ProtectedRoute><ParticipantImportPage /></ProtectedRoute>} />
      <Route path="/my-data" element={<ProtectedRoute><MyDataPage /></ProtectedRoute>} />
      <Route path="/lead-assignment" element={<ProtectedRoute><LeadAssignmentImportPage /></ProtectedRoute>} />
      <Route path="/performer" element={<ProtectedRoute performerAllowed><PerformerPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/finance-operations" element={<ProtectedRoute namedPerm="payments.approve"><FinanceOperationsPage /></ProtectedRoute>} />
      <Route path="/masters" element={<ProtectedRoute adminOnly><MastersPage /></ProtectedRoute>} />
      <Route path="/hierarchy" element={<ProtectedRoute adminOnly><ProgramHierarchyPage /></ProtectedRoute>} />
      <Route path="/merge-reviews" element={<ProtectedRoute adminOnly><MergeReviewsPage /></ProtectedRoute>} />
      <Route path="/admin/program-tracker/:programId" element={<ProtectedRoute adminOnly><ProgramDashboard /></ProtectedRoute>} />
      <Route path="/admin/:tabSegment" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/:tabSegment" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
      <Route path="/roles" element={<ProtectedRoute namedPerm="roles.manage"><RolesPage /></ProtectedRoute>} />
      <Route path="/reporting" element={<ProtectedRoute namedPerm={["reports.view_unique_people", "reports.team_performance", "reports.view_agent_performance"]}><ReportingPage /></ProtectedRoute>} />
      <Route path="/reporting/:tabSegment" element={<ProtectedRoute namedPerm={["reports.view_unique_people", "reports.team_performance", "reports.view_agent_performance"]}><ReportingPage /></ProtectedRoute>} />
      <Route path="/coach-overlap" element={<ProtectedRoute namedPerm="reports.view_unique_people"><CoachOverlapPage /></ProtectedRoute>} />
      <Route path="/unique-people" element={<ProtectedRoute adminOnly><UniquePeoplePage /></ProtectedRoute>} />
      <Route path="/finance" element={<ProtectedRoute namedPerm="finance.view_revenue"><FinancePage /></ProtectedRoute>} />
      <Route path="/finance/payments" element={<ProtectedRoute namedPerm="payments.verify"><FinancePage /></ProtectedRoute>} />
      <Route path="/finance/gst" element={<ProtectedRoute namedPerm="finance.view_revenue"><FinancePage /></ProtectedRoute>} />
      <Route path="/finance/refunds" element={<ProtectedRoute namedPerm="refunds.view_all"><FinancePage /></ProtectedRoute>} />
      <Route path="/it" element={<ProtectedRoute><ITDashboard /></ProtectedRoute>} />
      <Route path="/failed-payments" element={<ProtectedRoute><FailedPaymentsPage /></ProtectedRoute>} />
      <Route path="/refund-requests" element={<ProtectedRoute><RefundRequestsPage /></ProtectedRoute>} />
      <Route path="/pending-alignment" element={<ProtectedRoute adminOnly><PendingAlignmentPage /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute adminOnly><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/call-reports" element={<ProtectedRoute adminOnly><CallReportsPage /></ProtectedRoute>} />
      <Route path="/team-dashboard" element={<ProtectedRoute><TeamDashboardPage /></ProtectedRoute>} />
      <Route path="/focus" element={<ProtectedRoute><FocusModePage /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute namedPerm="security.view"><SecurityPage /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<Navigate to="/security?tab=audit-logs" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Prevent unhandled 403 rejections from crashing the React dev overlay.
  // Components with local .catch() blocks handle their own 403 UI; this is a
  // safety net for any call that slips through without one.
  useEffect(() => {
    const handle403 = (event) => {
      if (event.reason?.response?.status === 403) {
        event.preventDefault();
        console.warn("[RBAC] Access denied (403):", event.reason?.config?.url || "");
      }
    };
    window.addEventListener("unhandledrejection", handle403);
    return () => window.removeEventListener("unhandledrejection", handle403);
  }, []);

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
